import * as Api from '@ViewModels';
import Editor from '@monaco-editor/react';
import { css } from 'aphrodite';
import { observer, Provider } from 'mobx-react';
import { Tabs } from 'radix-ui';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import {
	useAdminAccountQuery,
	useCreateCustomPageStyleMutation,
	useGetCssByPageTypeQuery,
	useHostedBlogPagePreviewQuery,
	useUpdateCustomPageStyleMutation,
	invalidateCssByPageTypeQueries,
} from '../../../../queries';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../web/components/ConfirmationDialog';
import { LoadingLogo } from '../../../../web/components/LoadingLogo';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { IModalProps } from '../../../../web/components/Modal';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { NavigationBreadcrumbsBar } from '../../../../web/components/NavigationBreadcrumbsBar';
import { ArrowIcon } from '../../../../web/components/svgs/icons/ArrowIcon';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { HostedBlogPreview } from './HostedBlogPreview';
import { HostedBlogPreviewSplitView } from './HostedBlogPreviewSplitView';
import { styleSheet } from './styles';
import { useCssEditor } from './useCssEditor';

const TabValues = {
	Library: 'Blog Library',
	Individual: 'Individual Blog Post',
} as const;

const snapPercent = 33;

const DiscardChangesConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Discard Changes',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

export const BlogEditor = observer(function BlogEditor() {
	const { id: accountId } = useParams<{ id: string }>();
	const errorMessages = useErrorMessages();

	const impersonationContextRef = React.useRef(
		new ImpersonationContextViewModel({
			account: { id: accountId },
		})
	);

	const [cssContent, setCssContent] = React.useState('');
	const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
	const editorRef = React.useRef<any>(null);
	const debouncedCssContent = useDebounceValue(cssContent, 300);

	// Force Monaco Editor to update its content when CSS content changes
	React.useEffect(() => {
		if (editorRef.current) {
			editorRef.current.setValue(cssContent);
		}
	}, [cssContent]);

	const { data: account } = useAdminAccountQuery({
		accountId,
		meta: {
			toastErrorMessage: 'Error loading account',
		},
	});

	useEffect(() => {
		if (account) {
			impersonationContextRef.current.update({
				account,
			});
		}
	}, [account]);

	const saveMutation = useUpdateCustomPageStyleMutation({
		onSuccess: () => {
			toast.success('Saved changes successfully');
			setHasUnsavedChanges(false);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const createMutation = useCreateCustomPageStyleMutation({
		onSuccess: () => {
			invalidateCssByPageTypeQueries(pageType);
			toast.success('Saved changes successfully');
			setHasUnsavedChanges(false);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const [splitPercentage, setSplitPercentage] = React.useState<number>(snapPercent);
	const [isDraggingDivider, setIsDraggingDivider] = React.useState<boolean>(false);

	const TAB_VALUES = Object.values(TabValues);
	const [selectedTab, setSelectedTab] = React.useState<(typeof TabValues)[keyof typeof TabValues]>(TabValues.Library);
	const [discardConfirmationModalProps, setDiscardConfirmationModalProps] = React.useState<
		IModalProps<IConfirmationDialogOption<boolean>>
	>({
		isOpen: false,
		onRequestClose: () => {
			setDiscardConfirmationModalProps(value => ({ ...value, isOpen: false }));
		},
	});
	const pageType =
		selectedTab === TabValues.Library ? Api.CustomPageType.BlogPreviewPage : Api.CustomPageType.BlogIndividualPage;
	const {
		editorProps,
		isLoadingAllowedClasses: isLoadingCssEditorData,
		hasError,
		allowedClasses,
	} = useCssEditor({ pageType });

	const cssStyleQuery = useGetCssByPageTypeQuery({
		enabled: Boolean(allowedClasses),
		type: pageType,
		impersonationContext: impersonationContextRef.current.toJs(),
		refetchOnWindowFocus: false,
	});
	React.useEffect(() => {
		if (cssStyleQuery.data) {
			setHasUnsavedChanges(false);
			// This is a workaround to prevent the editor from retaining the previous css content
			// I'm not sure why this is necessary, but it seems to work
			requestAnimationFrame(() => {
				setCssContent(cssStyleQuery.data.css);
			});
		} else if (cssStyleQuery.isSuccess && cssStyleQuery.data === null && allowedClasses) {
			// No existing record — show scaffolded CSS from allowed classes
			const scaffoldedCss = allowedClasses.reduce((prevContent, cls) => {
				return `${prevContent}${prevContent ? '\n\n' : ''}${cls} {\n}`;
			}, '');
			setCssContent(scaffoldedCss);
			setHasUnsavedChanges(false);
		}
	}, [cssStyleQuery.data, cssStyleQuery.isSuccess, allowedClasses]);

	const htmlQuery = useHostedBlogPagePreviewQuery({
		page: pageType,
		impersonationContext: impersonationContextRef.current.toJs(),
		refetchOnWindowFocus: false,
	});

	const appBarHeaderPathComponentNameProvider = (pathComponent: string) => {
		if (account && account.id === pathComponent) {
			return account.companyName;
		}
		return pathComponent
			? pathComponent
					.split(' ')
					.filter(Boolean)
					.map(x => `${x.substring(0, 1).toLocaleUpperCase()}${x.substring(1)}`)
					.join(' ')
			: pathComponent;
	};
	const renderAppBarHeader = () => {
		return (
			<NavigationBreadcrumbsBar
				currentLocationName='Blog Template Editor'
				pathComponentNameProvider={appBarHeaderPathComponentNameProvider}
			/>
		);
	};

	const onTabChange = (tab: string) => {
		if (hasUnsavedChanges) {
			setDiscardConfirmationModalProps(value => ({
				...value,
				isOpen: true,
				onRequestClose: result => {
					setDiscardConfirmationModalProps(v => ({ ...v, isOpen: false }));
					if (result?.isDestructive) {
						// Clear CSS content immediately to prevent showing old tab's content
						setCssContent('');
						setSelectedTab(tab as typeof selectedTab);
					}
				},
			}));
		} else {
			// Clear CSS content immediately to prevent showing old tab's content
			setCssContent('');
			setSelectedTab(tab as typeof selectedTab);
		}
	};

	const disableSaving =
		saveMutation.isPending || createMutation.isPending || !cssContent || hasError || !hasUnsavedChanges;

	const isLoading = isLoadingCssEditorData || cssStyleQuery.isPending || htmlQuery.isPending;

	if (isLoading) {
		return <LoadingLogo size={100} />;
	}

	return (
		<Provider impersonationContext={impersonationContextRef.current}>
			<div className={`admin-blog-settings-container ${css(styleSheet.container)}`}>
				<MultiContainerHeader appBarHeader={renderAppBarHeader()} appBarPortalDestinationId={MastheadPortalId} />
				{account && !isLoadingCssEditorData && !cssStyleQuery.isPending ? (
					<>
						<Tabs.Root className={css(styleSheet.tabView)} value={selectedTab} onValueChange={onTabChange}>
							<Tabs.List className={css(styleSheet.tabViewTablist)}>
								<menu>
									{TAB_VALUES.map(tab => {
										return (
											<Tabs.Trigger
												className={css(styleSheet.tabViewItem, selectedTab === tab && styleSheet.tabViewSelectedItem)}
												key={tab}
												value={tab}
											>
												{tab}
											</Tabs.Trigger>
										);
									})}
								</menu>
								<button
									className={css(baseStyleSheet.ctaButton, styleSheet.saveButton)}
									disabled={disableSaving}
									onClick={() => {
										if (cssStyleQuery.data?.id) {
											saveMutation.mutate({
												id: cssStyleQuery.data.id,
												style: cssContent,
												impersonationContext: impersonationContextRef.current?.toJs(),
											});
										} else if (!cssStyleQuery.isFetching) {
											createMutation.mutate({
												style: {
													css: cssContent,
													type: pageType,
												},
												impersonationContext: impersonationContextRef.current?.toJs(),
											});
										}
									}}
								>
									<span>Save Changes</span>
								</button>
							</Tabs.List>

							<div className={css(styleSheet.body)}>
								<HostedBlogPreviewSplitView
									splitPercentage={splitPercentage}
									onSplitPercentageChange={setSplitPercentage}
									onIsDraggingDividerChange={setIsDraggingDivider}
									styles={[styleSheet.splitContainer]}
									leftToolbarNode={
										<div className={css(styleSheet.toolbarLeft)}>
											<div className={css(baseStyleSheet.flexCenter)}>{}</div>
											<button
												className={css(styleSheet.toolbarButton)}
												onClick={() => {
													setSplitPercentage(percent => (percent <= snapPercent ? 5 : snapPercent));
												}}
											>
												<ArrowIcon styles={[styleSheet.rotated]} />
											</button>
										</div>
									}
									leftNode={
										<div className={css(styleSheet.editorContainer)}>
											<Editor
												{...editorProps}
												height='100%'
												loading={<LoadingSpinner type='small' />}
												value={cssContent}
												onMount={editor => {
													editorRef.current = editor;
													// Only set value if we have actual content (not empty initial state)
													if (cssContent) {
														editor.setValue(cssContent);
													}
												}}
												onChange={content => {
													setHasUnsavedChanges(true);
													setCssContent(content);
												}}
												width='100%'
												theme='vs-dark'
												key={selectedTab}
											/>
										</div>
									}
									rightToolbarNode={
										<div className={css(styleSheet.toolbarRight)}>
											<button
												className={css(styleSheet.toolbarButton)}
												onClick={() => {
													setSplitPercentage(percent => (percent >= snapPercent ? 95 : snapPercent));
												}}
											>
												<ArrowIcon />
											</button>
										</div>
									}
									rightNode={
										<div className={css(styleSheet.previewContainer)}>
											<HostedBlogPreview
												cssContent={debouncedCssContent}
												htmlContent={htmlQuery.data || ''}
												disabled={isDraggingDivider}
												disableClicks={true}
											/>
										</div>
									}
								/>
							</div>
						</Tabs.Root>
					</>
				) : null}
			</div>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={discardConfirmationModalProps}
				options={DiscardChangesConfirmationOptions}
				title={
					<>
						<div>You have unsaved changes.</div>
						<div>Are you sure you want to discard these changes?</div>
					</>
				}
			/>
		</Provider>
	);
});
